import { createApi } from "@reduxjs/toolkit/query/react";
import {
  AdditionalDetailsPayload,
  QuizRunPayload,
  UnifiedAssessmentResponse,
} from "@src/pages/quiz/quiz-screen/data.types";
import {
  QuizGroupResponse,
  StudentAnswerApiResponse,
  StudentMyQuizResponse,
  StudentQuizApiResponse,
} from "@src/pages/student/data.type";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import { InitialState } from "@src/app/studentQuizSlice";
import { PaginationType } from "@src/types/CommonTypes";
import { isElectronApp } from "@src/utils/envUtils";

export interface AssessmentCard {
  assessmentId: string;
  assessmentName: string;
  trainerName: string;
  startDate: string;
  endDate: string;
  status: string;
  malPractice: boolean;
  allowOnMobile: boolean;
  studentMalPracticeCount: number;
  duration?: number | string;
  assessmentType: string;
  quizType?: string;
  attemptId?: number;
}

export interface AssessmentMetaData {
  totalAssessmentCount: number;
  completedAssessmentCount: number;
  assessmentCardResponseList: { content: AssessmentCard[] } & PaginationType;
}

export interface GetAllQuizListResponse {
  statusCode: number;
  httpStatus: string;
  response: AssessmentMetaData;
}

export interface SubmitQuestionPayload {
  versionId?: string;
  language: string | null;
  questionProgramType?: string;
  assessmentId?: string;
  questionSubmitDuration?: number;
  questionNo?: string | number;
  userId?: string;
  batchId?: string;
  questionId?: string;
  marks?: number;
  userCode?: string;
  options?: string[];
  isThickClient?: boolean;
  answer?: string;
  blankOptions?: Record<string, string>;
  matching?: Record<string, string>;
  numericValue?: number;
  numerator?: number;
  denominator?: number;
  blanksOptionsSet?: Record<string, string[]>;
  sectionName?: string;
  student_text?: string;
  passage_text?: string;
  attemptId?: string;
  promptTokenBalance?: number;
  cognitiveAssessmentSummary?: Record<string, any>;
  result?: boolean | string;
  studentMarks?: number;
  output?: string;
}

export interface SubmitQuestionApiResponse extends APIResponse {
  response: UnifiedAssessmentResponse;
}

export interface AiCodeEvaluatorRequest {
  agent_id: string;
  language: string;
  keywords: string[];
  evaluationMode: string;
  problemStatement: string;
  candidateCode: string;
  marks?: number;
  max_marks?: number;
  total_marks?: number;
  message?: string;
}

export interface AiCodeEvaluatorResponse {
  id: string;
  status: string;
  output_payload?: {
    type?: string;
    grade?: string;
    result?: string;
    overall_score?: number;
    score?: number;
    maxscore?: number;
    max_score?: number;
    marks_scored?: number;
    marks?:
      | {
          scored?: number;
          max?: number;
        }
      | number;
    mark?: number;
    code_output?: string;
    total_marks?: number;
    max_marks?: number;
    questionMarks?: number;
    summary?: string;
    keyword_check?: {
      score?: number;
      max_score?: number;
      all_present?: boolean;
      present?: string[];
      missing?: string[];
      details?: Array<{
        keyword: string;
        status: string;
        evidence?: string;
      }>;
    };
    attribute_coverage?: {
      expected?: string[];
      found?: Array<{ name: string; value: string }>;
      missing?: string[];
      notes?: string;
    };
    ai_tests?: {
      score?: number;
      max_score?: number;
      total?: number;
      passed?: number;
      failed?: number;
      cases?: Array<{
        id: string;
        category?: string;
        description?: string;
        expected?: string;
        status?: string;
        evidence?: string;
      }>;
    };
    problem_fit?: {
      score?: number;
      max_score?: number;
      status?: string;
      requirements_met?: string[];
      requirements_partially_met?: string[];
      requirements_not_met?: string[];
      issues?: string[];
    };
    code_quality?: {
      score?: number;
      max_score?: number;
      status?: string;
      issues?: string[];
    };
    strengths?: string[];
    weaknesses?: string[];
    recommendations?: string[];
    [key: string]: any;
  };
  [key: string]: any;
}

export const StudentQuizApiSlice = createApi({
  reducerPath: "studentQuizApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getQuizById: builder.query<StudentQuizApiResponse, string>({
      query: (quizId) => ({
        url: `quiz?quizId=${quizId}`,
        method: "GET",
      }),
    }),
    getAssessmentForStudent: builder.query<
      StudentQuizApiResponse,
      {
        studentId: string;
        assessmentId: string;
        batchId: string;
        quizCategoryType?: string;
        isThickClient?: boolean;
      }
    >({
      query: ({
        studentId,
        assessmentId,
        batchId,
        quizCategoryType = "assessment",
        isThickClient,
      }) => {
        const effectiveIsThickClient =
          isThickClient !== undefined ? isThickClient : isElectronApp();
        const thickClientQuery =
          quizCategoryType === "assessment"
            ? `&isThickClient=${effectiveIsThickClient}`
            : "";

        return {
          url:
            quizCategoryType == "assessment"
              ? `assessment/get-assessment-student?studentId=${studentId}&assessmentId=${assessmentId}&batchId=${batchId}${thickClientQuery}`
              : quizCategoryType == "tournament"
                ? `tournament/get-tournament-student?tournamentId=${assessmentId}`
                : `quiz/get-quiz-student?studentId=${studentId}&quizId=${assessmentId}`,
          method: "GET",
          timeout: 45000,
        };
      },
    }),
    getAllQuizListForStudent: builder.query<
      GetAllQuizListResponse,
      {
        batchId: string;
        studentId: string;
        parentId: string;
        filterValue: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({
        batchId,
        studentId,
        parentId,
        pageNo,
        pageSize,
        filterValue,
      }) => ({
        url: `assessment/getallassessment?batchId=${batchId}&studentId=${studentId}&parentId=${parentId}&pageNo=${pageNo}&pageSize=${pageSize}&filter=${filterValue}`,
        method: "GET",
      }),
    }),
    submitQuestion: builder.mutation<
      SubmitQuestionApiResponse,
      { payload: SubmitQuestionPayload | FormData; quizCategoryType: string }
    >({
      query: ({ payload, quizCategoryType }) => ({
        url:
          quizCategoryType == "assessment"
            ? `execution/execute-assessment-question`
            : quizCategoryType == "tournament"
              ? `execution/execute-tournament-questions`
              : `execution/execute-quiz-questions`,
        method: "POST",
        body: payload,
        timeout: 90000,
      }),
    }),
    submitQuiz: builder.mutation<
      any,
      {
        batchId: string;
        assessmentId: string;
        studentId: string;
        quizCategoryType: string;
        timeUtilized: number | string;
        malPractice?: boolean;
        malPracticeCount?: number;
        additionalDetails?: AdditionalDetailsPayload | null;
        organizationData?: InitialState["organizationData"];
        attemptId?: string;
        isThickClient?: boolean;
      }
    >({
      query: ({
        batchId,
        assessmentId,
        studentId,
        quizCategoryType,
        timeUtilized,
        malPractice,
        malPracticeCount,
        additionalDetails,
        organizationData,
        attemptId,
        isThickClient,
      }) => {
        const body = {
          organization: organizationData?.organization?.label,
          subOrganization: organizationData?.subOrganization?.label,
          branch: organizationData?.branch?.label,
          additionalDetails,
          malPracticeCount,
          attemptId,
          ...(quizCategoryType === "quiz" || quizCategoryType === "quizGroup"
            ? { quizDuration: timeUtilized }
            : {}),
        };

        const thickClientQuery =
          quizCategoryType === "assessment" && isThickClient !== undefined
            ? `&isThickClient=${isThickClient}`
            : "";

        return {
          url:
            quizCategoryType === "assessment"
              ? `assessment/add-assessment-result?batchId=${batchId}&assessmentId=${assessmentId}&studentId=${studentId}&malPractice=${malPractice}&malPracticeCount=${malPracticeCount}&timeTaken=${timeUtilized}${attemptId ? `&attemptId=${attemptId}` : ""}${thickClientQuery}`
              : quizCategoryType === "tournament"
                ? `tournament/add-tournament-results?tournamentId=${assessmentId}&tournamentDuration=${timeUtilized}&malPractice=${malPractice}`
                : `quiz/add-quiz-results?quizId=${assessmentId}&malPractice=${malPractice}`,
          method: "POST",
          body: quizCategoryType !== "assessment" && body,
        };
      },
    }),
    runCode: builder.mutation<any, QuizRunPayload>({
      query: (payload) => ({
        url: `execution/run`,
        method: "POST",
        body: payload,
        timeout: 45000,
      }),
    }),
    runAiCodeEvaluator: builder.mutation<
      AiCodeEvaluatorResponse,
      AiCodeEvaluatorRequest
    >({
      async queryFn(payload) {
        const configuredUrl =
          import.meta.env.VITE_AI_AGENT_RUN_URL ||
          import.meta.env.VITE_AI_CODE_EVALUATOR_URL ||
          "http://103.182.211.219:30805/api/v1/agents/run";
        const apiKey =
          import.meta.env.VITE_AI_AGENT_API_KEY ||
          "VLJwNdORFB2D5xpbnTrWPjacR0h5Fnm1lKSz_cG-6O4=";

        let effectiveUrl = configuredUrl;
        if (effectiveUrl.endsWith("?agent_id=")) {
          effectiveUrl = `${effectiveUrl}${payload.agent_id || "code-problem-evaluator"}`;
        } else if (
          !effectiveUrl.includes("agent_id=") &&
          effectiveUrl.includes("agent-prompt/v1/run")
        ) {
          effectiveUrl = `${effectiveUrl}?agent_id=${payload.agent_id || "code-problem-evaluator"}`;
        }

        const makeFetch = async (url: string) => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 90000);
          try {
            const res = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": apiKey,
              },
              body: JSON.stringify(payload),
              signal: controller.signal,
            });
            clearTimeout(timeoutId);
            return res;
          } catch (e) {
            clearTimeout(timeoutId);
            throw e;
          }
        };

        try {
          const res = await makeFetch(effectiveUrl);

          if (!res.ok) {
            const errorText = await res.text();
            let parsedError: any = errorText;
            try {
              parsedError = JSON.parse(errorText);
            } catch {}
            return {
              error: {
                status: res.status,
                data: parsedError,
              },
            };
          }

          const data = (await res.json()) as AiCodeEvaluatorResponse;
          return { data };
        } catch (err: any) {
          return {
            error: {
              status: "FETCH_ERROR",
              error:
                err?.message || "Failed to evaluate code with AI evaluator",
            },
          };
        }
      },
    }),
    getQuizResponseForStudentAnswers: builder.query<
      { response: StudentAnswerApiResponse },
      {
        studentId: string;
        assessmentId: string;
        batchId: string;
        quizCategoryType: string;
      }
    >({
      query: ({ studentId, assessmentId, batchId, quizCategoryType }) => ({
        url:
          quizCategoryType === "assessment"
            ? `assessment/get-student-response?studentId=${studentId}&assessmentId=${assessmentId}&batchId=${batchId}`
            : quizCategoryType === "tournament"
              ? `tournament/get-student-tournament-questionsResponse?tournamentId=${assessmentId}`
              : `quiz/get-student-quiz-questions?quizId=${assessmentId}`,
        method: "GET",
        timeout: 45000,
      }),
    }),
    joinStudentQuiz: builder.mutation<any, { quizCode: string }>({
      query: ({ quizCode }) => ({
        url: `student/join-quiz?quizCode=${encodeURIComponent(quizCode)}`,
        method: "PATCH",
      }),
    }),
    joinStudentQuizGroup: builder.mutation<any, { groupCode: string }>({
      query: ({ groupCode }) => ({
        url: `quiz-group?groupCode=${encodeURIComponent(groupCode)}`,
        method: "PUT",
      }),
    }),
    getMyQuizForStudent: builder.query<
      StudentMyQuizResponse,
      { status: string; pageNo: number; pageSize: number; scope: string }
    >({
      query: ({ status, pageNo, pageSize, scope }) => ({
        url: `student/get-quizzes?status=${status}&pageNo=${pageNo}&pageSize=${pageSize}&scope=${scope}`,
        method: "GET",
      }),
    }),
    getQuizzesForGroup: builder.query<
      StudentMyQuizResponse,
      { status: string; quizGroupId: string; pageNo: number; pageSize: number }
    >({
      query: ({ status, pageNo, pageSize, quizGroupId }) => ({
        url: `student/get-group-quizzes?status=${status}&quizGroupId=${quizGroupId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getQuizGroupForStudent: builder.query<
      QuizGroupResponse,
      { pageNo: number; pageSize: number }
    >({
      query: ({ pageNo, pageSize }) => ({
        url: `quiz-group/get-all-quiz-groups?pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getStudentQuizStatus: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: string;
      },
      { quizId: string }
    >({
      query: ({ quizId }) => ({
        url: `quiz/get-quiz-status-student`,
        method: "GET",
        params: { quizId },
      }),
    }),
    getAssessmentStatusForStudent: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: string;
      },
      { studentId: string; assessmentId: string; batchId: string }
    >({
      query: ({ studentId, assessmentId, batchId }) => ({
        url: `assessment/get-student-assessment-status?studentId=${studentId}&assessmentId=${assessmentId}&batchId=${batchId}`,
        method: "GET",
      }),
    }),
    sendAdditionalDetails: builder.mutation<
      any,
      { quizId: string; payload: AdditionalDetailsPayload }
    >({
      query: ({ quizId, payload }) => ({
        url: `quiz/add-student-additional-details?quizId=${quizId}`,
        method: "POST",
        body: payload,
      }),
    }),
    submitSection: builder.mutation<
      any,
      {
        quizId?: string;
        assessmentId?: string;
        sectionName: string;
        studentId?: string;
        sectionIndex?: number;
        timeTaken?: number | string;
        attemptId?: string | number;
        batchId?: string;
        quizCategoryType?: string;
        cognitiveAssessmentSummary?: any;
        isThickClient?: boolean;
      }
    >({
      query: ({
        quizId,
        assessmentId,
        sectionName,
        studentId,
        sectionIndex,
        timeTaken,
        attemptId,
        batchId,
        quizCategoryType,
        cognitiveAssessmentSummary,
        isThickClient,
      }) => {
        const bodyPayload: Record<string, any> = {
          quizId: quizId || assessmentId,
          sectionName,
          ...(studentId && { studentId }),
          ...(sectionIndex !== undefined && { sectionIndex }),
          ...(timeTaken !== undefined &&
            timeTaken !== null && {
              timeTaken:
                typeof timeTaken === "string"
                  ? parseFloat(timeTaken) || 0
                  : timeTaken,
            }),
          ...(attemptId !== undefined &&
            attemptId !== null && {
              attemptId:
                typeof attemptId === "string"
                  ? parseInt(attemptId, 10) || 0
                  : attemptId,
            }),
          ...(cognitiveAssessmentSummary && { cognitiveAssessmentSummary }),
          ...(isThickClient !== undefined && { isThickClient }),
        };

        if (quizCategoryType === "assessment") {
          let url = `assessment/submit-section?assessmentId=${assessmentId}&sectionName=${encodeURIComponent(sectionName)}&batchId=${batchId}`;
          if (timeTaken !== undefined && timeTaken !== null) {
            url += `&timeTaken=${timeTaken}`;
          }
          if (attemptId) {
            url += `&attemptId=${attemptId}`;
          }
          if (isThickClient !== undefined) {
            url += `&isThickClient=${isThickClient}`;
          }
          return {
            url,
            method: "POST",
            body: bodyPayload,
          };
        }
        let url = `quiz/submit-section?quizId=${quizId}&sectionName=${encodeURIComponent(sectionName)}`;
        if (attemptId) {
          url += `&attemptId=${attemptId}`;
        }
        return {
          url,
          method: "POST",
          body: bodyPayload,
        };
      },
    }),
    checkDevOpsTask: builder.mutation<
      {
        statusCode: number;
        httpStatus: string;
        response: {
          exitCode: number;
          output: string;
          passed: boolean;
        };
      },
      {
        user: string;
        testId: string;
        checkScript: string;
        questionId?: string;
      }
    >({
      queryFn: async (payload) => {
        try {
          const terminalBase =
            import.meta.env.VITE_TEST_FRESHERS_WS_TERMINAL_BASE_URL ||
            "http://localhost:8092";
          const runnerBase =
            import.meta.env.VITE_TEST_FRESHERS_RUN_BASE_URL ||
            "http://localhost:8092/api";

          const cleanTerminalBase = terminalBase
            .replace(/^wss?:\/\//, "http://")
            .replace(/\/$/, "");
          const cleanRunnerBase = runnerBase.replace(/\/$/, "");

          // Connect directly to local runner on localhost:8092 (/api/devops/verify)
          const targetUrl = cleanRunnerBase.endsWith("/api")
            ? `${cleanRunnerBase}/devops/verify`
            : `${cleanTerminalBase}/api/devops/verify`;

          const response = await fetch(targetUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const errText = await response.text();
            let parsedErr: any = null;
            try {
              parsedErr = JSON.parse(errText);
            } catch {}
            return {
              error: {
                status: response.status,
                data: parsedErr || {
                  message: errText || "Verification failed on localhost:8092",
                },
              },
            };
          }

          const rawData = await response.json();
          const resultObj = rawData?.response || rawData;
          const isPassed =
            typeof resultObj.passed === "boolean"
              ? resultObj.passed
              : resultObj.exitCode === 0;
          const output = resultObj.output ?? resultObj.message ?? "";
          const exitCode = resultObj.exitCode ?? (isPassed ? 0 : 1);

          return {
            data: {
              statusCode: 200,
              httpStatus: "OK",
              response: {
                exitCode,
                output,
                passed: isPassed,
              },
            },
          };
        } catch (error: any) {
          return {
            error: {
              status: "FETCH_ERROR",
              data: {
                message:
                  error?.message ||
                  "Unable to connect to local verification runner on http://localhost:8092. Please make sure the service is running.",
              },
            },
          };
        }
      },
    }),
  }),
});
export const {
  useGetQuizByIdQuery,
  useGetAssessmentForStudentQuery,
  useLazyGetAllQuizListForStudentQuery,
  useSubmitQuestionMutation,
  useSubmitQuizMutation,
  useGetMyQuizForStudentQuery,
  useLazyGetMyQuizForStudentQuery,
  useLazyGetQuizGroupForStudentQuery,
  useJoinStudentQuizMutation,
  useRunCodeMutation,
  useRunAiCodeEvaluatorMutation,
  useJoinStudentQuizGroupMutation,
  useLazyGetQuizResponseForStudentAnswersQuery,
  useGetQuizzesForGroupQuery,
  useLazyGetQuizzesForGroupQuery,
  useLazyGetStudentQuizStatusQuery,
  useLazyGetAssessmentStatusForStudentQuery,
  useSendAdditionalDetailsMutation,
  useSubmitSectionMutation,
  useCheckDevOpsTaskMutation,
} = StudentQuizApiSlice;
